"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { FileText } from "lucide-react";
function GuidesCarouselConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] animate-pulse bg-secondary/20 border-none" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center text-muted-foreground py-12">
        <p>Unable to load guides at this time.</p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center text-muted-foreground py-12">
        <p>No guides are currently available.</p>
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: false
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
            <Card className="h-full flex flex-col bg-secondary/30 border-none shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden rounded-2xl">
              <Link to={product.link} className="block relative aspect-[4/5] bg-background/50 overflow-hidden group">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-primary/20">
                    <FileText className="w-24 h-24" />
                  </div>}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300" />
              </Link>
              
              <CardHeader className="flex-grow pb-2">
                <Link to={product.link}>
                  <CardTitle className="font-serif text-xl text-primary line-clamp-2 hover:text-primary/80 transition-colors">
                    {product.title}
                  </CardTitle>
                </Link>
                {product.short_description && <div className="text-sm text-muted-foreground mt-2 line-clamp-2 font-sans" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}
              </CardHeader>

              <CardContent className="pb-4">
                <div className="text-2xl font-bold text-primary font-sans">
                  ${product.price?.toFixed(2) || "0.00"}
                </div>
              </CardContent>

              <CardFooter className="pt-0">
                <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full font-semibold py-6 text-lg shadow-sm" text="Quick Buy" showQuantity={false} />
              </CardFooter>
            </Card>
          </CarouselItem>)}
      </CarouselContent>
      <div className="flex justify-center gap-4 mt-12">
        <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground" />
        <CarouselNext className="static translate-y-0 translate-x-0 size-12 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground" />
      </div>
    </Carousel>;
}
export default function FeaturedSupportGuides() {
  const wp_query_params = {
    per_page: 8
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="featured-support-guides" className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif text-primary mb-6">
            Practical Tools for Your Journey
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans">
            Downloadable guides and resources designed specifically for Australian parents and teachers supporting children with PDA.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8
      }}>
          <GuidesCarouselConsumer />
        </EcommerceProductsProvider>

        <div className="mt-16 text-center">
          <Link to="/ShopPage" className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary border-2 border-primary rounded-full hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
            View All Guides
          </Link>
        </div>
      </div>
    </section>;
}